<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-header mb-[20px] md:mb-[25px] flex items-center justify-between"
    >
      <div class="trezo-card-title">
        <h5 class="mb-0">
          {{ searchResults.length }}
          <span class="text-gray-500 dark:text-gray-400"
            >{{ directT('search.resultsFound') }} </span
          >
          "{{ searchQuery }}"
        </h5>
      </div>
    </div>
    <div class="trezo-card-content">
      <!-- Search input for in-page search -->
      <div class="mb-[20px]">
        <form @submit.prevent="performSearch" class="flex">
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="directT('common.searchHere')"
            class="bg-gray-50 border border-gray-50 h-[44px] rounded-l-md w-full block text-black pt-[11px] pb-[12px] px-[13px] md:px-[16px] placeholder:text-gray-500 outline-0 dark:bg-[#15203c] dark:text-white dark:border-[#15203c] dark:placeholder:text-gray-400"
          />
          <button
            type="submit"
            class="bg-primary-500 hover:bg-primary-600 text-white h-[44px] px-4 rounded-r-md transition-all"
          >
            <i class="material-symbols-outlined !text-[20px]"> search </i>
          </button>
        </form>
      </div>
      
      <!-- Loading indicator -->
      <div v-if="loading" class="text-center py-5">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent"></div>
        <p class="mt-2 text-gray-500 dark:text-gray-400">{{ directT('search.searching') }}</p>
      </div>

      <!-- No results message -->
      <div v-else-if="searchResults.length === 0 && searchQuery" class="text-center py-5">
        <i class="material-symbols-outlined !text-[50px] text-gray-400 mb-2">search_off</i>
        <p class="text-gray-500 dark:text-gray-400">{{ directT('search.noResults') }}</p>
      </div>

      <!-- Search results -->
      <div v-else-if="searchResults.length > 0">
        <div
          v-for="(result, index) in searchResults"
          :key="index"
          class="border border-gray-100 dark:border-[#172036] mb-[15px] rounded-md p-[20px]"
        >
          <span
            class="block mb-[12px] font-semibold text-[17px] text-black dark:text-white"
          >
            {{ result.title }}
          </span>
          <p>{{ result.content }}</p>
          <div class="mt-3">
            <router-link
              :to="result.link"
              class="text-primary-500 hover:underline inline-flex items-center"
            >
              {{ directT('search.viewDetails') }}
              <i class="material-symbols-outlined !text-[16px] ml-1">arrow_forward</i>
            </router-link>
          </div>
        </div>
      </div>
      <!-- Placeholder for empty state -->
      <div v-else class="text-center py-5 text-gray-400">
        {{ directT('search.startSearching') }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for nested translation objects
type TranslationObject = { [key: string]: string | TranslationObject };

interface SearchResult {
  title: string;
  content: string;
  link: string;
}

export default defineComponent({
  name: "SearchPage",
  setup() {
    const route = useRoute();
    const searchQuery = ref("");
    const searchResults = ref<SearchResult[]>([]);
    const loading = ref(false);

    // i18n setup
    const { locale } = useI18n();
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject | string = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || typeof result === 'string' || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : String(enResult);
        }
        if (typeof result === 'string') break;
        result = result[part as keyof typeof result] as TranslationObject | string;
      }
      return typeof result === 'string' ? result : String(result);
    };

    // Perform the search
    const performSearch = async () => {
      if (!searchQuery.value.trim()) return;
      
      loading.value = true;
      
      try {
        // Simulate search with mock data
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Mock search results based on query
        if (searchQuery.value.toLowerCase().includes('customer') || 
            searchQuery.value.toLowerCase().includes('לקוח')) {
          searchResults.value = [
            {
              title: String(directT('search.results.customers.title')),
              content: String(directT('search.results.customers.content')),
              link: '/crm/customers'
            },
            {
              title: String(directT('search.results.customerSettings.title')),
              content: String(directT('search.results.customerSettings.content')),
              link: '/settings/customers-settings'
            }
          ];
        } else if (searchQuery.value.toLowerCase().includes('contact') || 
                  searchQuery.value.toLowerCase().includes('אנשי קשר') ||
                  searchQuery.value.toLowerCase().includes('איש קשר')) {
          searchResults.value = [
            {
              title: String(directT('search.results.contacts.title')),
              content: String(directT('search.results.contacts.content')),
              link: '/crm/contacts'
            },
            {
              title: String(directT('search.results.apps.contacts.title')),
              content: String(directT('search.results.apps.contacts.content')),
              link: '/apps/contacts'
            }
          ];
        } else if (searchQuery.value.toLowerCase().includes('lead') || 
                  searchQuery.value.toLowerCase().includes('ליד')) {
          searchResults.value = [
            {
              title: String(directT('search.results.leads.title')),
              content: String(directT('search.results.leads.content')),
              link: '/crm/leads'
            },
            {
              title: String(directT('search.results.leadSettings.title')),
              content: String(directT('search.results.leadSettings.content')),
              link: '/settings/leads-settings'
            }
          ];
        } else if (searchQuery.value.toLowerCase().includes('dashboard') || 
                  searchQuery.value.toLowerCase().includes('לוח בקרה')) {
          searchResults.value = [
            {
              title: String(directT('search.results.dashboard.title')),
              content: String(directT('search.results.dashboard.content')),
              link: '/dashboards/saas'
            }
          ];
        } else if (searchQuery.value.toLowerCase().includes('email') || 
                  searchQuery.value.toLowerCase().includes('mail') ||
                  searchQuery.value.toLowerCase().includes('דואר') ||
                  searchQuery.value.toLowerCase().includes('אימייל')) {
          searchResults.value = [
            {
              title: String(directT('search.results.email.title')),
              content: String(directT('search.results.email.content')),
              link: '/apps/email/inbox'
            }
          ];
        } else if (searchQuery.value.toLowerCase().includes('calendar') || 
                  searchQuery.value.toLowerCase().includes('לוח שנה')) {
          searchResults.value = [
            {
              title: String(directT('search.results.calendar.title')),
              content: String(directT('search.results.calendar.content')),
              link: '/apps/calendar'
            }
          ];
        } else if (searchQuery.value.toLowerCase().includes('chat') || 
                  searchQuery.value.toLowerCase().includes('צ׳אט')) {
          searchResults.value = [
            {
              title: String(directT('search.results.chat.title')),
              content: String(directT('search.results.chat.content')),
              link: '/apps/chat'
            }
          ];
        } else {
          searchResults.value = [];
        }
      } catch (error) {
        console.error('Search error:', error);
        searchResults.value = [];
      } finally {
        loading.value = false;
      }
    };

    // Get search query from URL
    onMounted(() => {
      const queryParam = route.query.q;
      if (queryParam && typeof queryParam === 'string') {
        searchQuery.value = queryParam;
        performSearch();
      }
      
      // Listen for global search events
      window.addEventListener('globalSearch', ((event: any) => {
        searchQuery.value = event.detail.query;
        performSearch();
      }));
    });

    // Watch for changes in route query
    watch(() => route.query.q, (newQuery) => {
      if (newQuery && typeof newQuery === 'string') {
        searchQuery.value = newQuery;
        performSearch();
      }
    });

    return {
      searchQuery,
      searchResults,
      loading,
      performSearch,
      directT
    };
  }
});
</script>
